import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Box, IconButton, SxProps, Theme } from '@mui/material';
import { useForkRef } from '@mui/material/utils';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ScrollEdgeFade from './ScrollEdgeFade';

type VerticalOverflowScrollerProps = {
  children: React.ReactNode;
  sx?: SxProps<Theme>;
  scrollerSx?: SxProps<Theme>;
  scrollerRef?: React.Ref<HTMLDivElement>;
  scrollerProps?: Pick<React.HTMLAttributes<HTMLDivElement>, 'id' | 'role' | 'aria-labelledby' | 'tabIndex'>;
  contentSx?: SxProps<Theme>;
  fadeColor?: string;
  fadeHeight?: number;
  ariaLabel?: string;
  showArrows?: boolean;
  onEndReached?: () => void;
  onEndReachedThreshold?: number;
};

const EDGE_EPSILON = 6;

const VerticalOverflowScroller: React.FC<VerticalOverflowScrollerProps> = ({
  children,
  sx,
  scrollerSx,
  scrollerRef: externalScrollerRef,
  scrollerProps,
  contentSx,
  fadeColor = '#101219',
  fadeHeight = 38,
  ariaLabel,
  showArrows = false,
  onEndReached,
  onEndReachedThreshold = 120,
}) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const handleScrollerRef = useForkRef(scrollerRef, externalScrollerRef);
  const contentRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ top: false, bottom: false });

  const updateEdges = useCallback(() => {
    const scroller = scrollerRef.current;
    const content = contentRef.current;
    if (!scroller) return;

    const maxScrollTop = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    const distanceFromEnd = maxScrollTop - scroller.scrollTop;
    const scrollerBounds = scroller.getBoundingClientRect();
    const contentBounds = content?.getBoundingClientRect();
    const hasOverflow = maxScrollTop > EDGE_EPSILON;
    const nextEdges = {
      top: hasOverflow && (contentBounds
        ? contentBounds.top < scrollerBounds.top - EDGE_EPSILON
        : scroller.scrollTop > EDGE_EPSILON),
      bottom: hasOverflow && (contentBounds
        ? contentBounds.bottom > scrollerBounds.bottom + EDGE_EPSILON
        : scroller.scrollTop < maxScrollTop - EDGE_EPSILON),
    };

    setEdges((current) => (
      current.top === nextEdges.top && current.bottom === nextEdges.bottom
        ? current
        : nextEdges
    ));
    if (onEndReached && distanceFromEnd <= onEndReachedThreshold) {
      onEndReached();
    }
  }, [onEndReached, onEndReachedThreshold]);

  const scrollByDirection = useCallback((direction: -1 | 1) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.scrollBy({
      top: direction * Math.max(180, scroller.clientHeight * 0.72),
      behavior: 'smooth',
    });
  }, []);

  useLayoutEffect(() => {
    updateEdges();
    // Hiding or revealing a pane can resize several nested observers at once.
    // Apply React updates on the next frame, outside ResizeObserver delivery.
    let frame = 0;
    const scheduleEdges = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updateEdges);
    };
    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(scheduleEdges);
    if (resizeObserver) {
      if (scrollerRef.current) resizeObserver.observe(scrollerRef.current);
      if (contentRef.current) resizeObserver.observe(contentRef.current);
    }

    window.addEventListener('resize', updateEdges);
    return () => {
      cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateEdges);
    };
  }, [updateEdges]);

  const arrowSx = {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 4,
    width: 32,
    height: 32,
    bgcolor: 'rgba(14,16,22,0.78)',
    color: 'rgba(255,255,255,0.76)',
    border: '1px solid rgba(255,255,255,0.15)',
    boxShadow: '0 2px 9px rgba(0,0,0,0.26)',
    transition: 'opacity 150ms ease, background-color 150ms ease, color 150ms ease, box-shadow 150ms ease',
    '&:hover, &.Mui-focusVisible': {
      bgcolor: '#fff',
      color: '#0e0c0d',
      boxShadow: '0 3px 12px rgba(0,0,0,0.38)',
    },
  } as const;

  return (
    <Box
      sx={[
        {
          position: 'relative',
          // Keep fades and arrows below popups outside this scroller.
          isolation: 'isolate',
          minHeight: 0,
          minWidth: 0,
          overflow: 'hidden',
          '& .overflow-scroll-arrow': {
            opacity: 0,
            pointerEvents: 'none',
          },
          '&:hover .overflow-scroll-arrow, &:focus-within .overflow-scroll-arrow': {
            opacity: 1,
            pointerEvents: 'auto',
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box
        {...scrollerProps}
        ref={handleScrollerRef}
        className="overflow-scroll-viewport"
        onScroll={updateEdges}
        aria-label={ariaLabel}
        sx={[
          {
            width: '100%',
            height: '100%',
            minHeight: 0,
            boxSizing: 'border-box',
            overflowY: 'auto',
            overflowX: 'hidden',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
          },
          ...(Array.isArray(scrollerSx) ? scrollerSx : [scrollerSx]),
        ]}
      >
        <Box
          ref={contentRef}
          sx={[
            { width: '100%', minHeight: 'min-content' },
            ...(Array.isArray(contentSx) ? contentSx : [contentSx]),
          ]}
        >
          {children}
        </Box>
      </Box>

      {edges.top && (
        <ScrollEdgeFade edge="top" color={fadeColor} height={fadeHeight} />
      )}
      {edges.bottom && (
        <ScrollEdgeFade edge="bottom" color={fadeColor} height={fadeHeight} />
      )}
      {showArrows && edges.top && (
        <IconButton
          className="overflow-scroll-arrow"
          type="button"
          aria-label={`Scroll ${ariaLabel || 'options'} up`}
          onClick={() => scrollByDirection(-1)}
          sx={{ ...arrowSx, top: 2 }}
        >
          <KeyboardArrowUpIcon sx={{ fontSize: 23 }} />
        </IconButton>
      )}
      {showArrows && edges.bottom && (
        <IconButton
          className="overflow-scroll-arrow"
          type="button"
          aria-label={`Scroll ${ariaLabel || 'options'} down`}
          onClick={() => scrollByDirection(1)}
          sx={{ ...arrowSx, bottom: 2 }}
        >
          <KeyboardArrowDownIcon sx={{ fontSize: 23 }} />
        </IconButton>
      )}
    </Box>
  );
};

export default VerticalOverflowScroller;
